/**
 * @fileoverview リモート操作（ライブ画面）
 * @description WebSocket中継(/api/mdm/rc)経由で端末の画面フレーム(JPEG)を受信して
 *   Canvasに描画し、クリック/ドラッグを正規化座標のタップ/スワイプとして端末へ送る。
 *   別ウィンドウ(popup)で開かれる想定。URL: /devices/:deviceId/remote
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getToken } from '../services/api';

const API = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
// https://api.unison-sea.com/api → wss://api.unison-sea.com/api/mdm/rc
const WS_BASE = API.replace(/^http/, 'ws').replace(/\/api$/, '/api/mdm/rc');

type Status = 'connecting' | 'waiting' | 'streaming' | 'offline' | 'closed';

const RemoteControlPage: React.FC = () => {
  const { deviceId = '' } = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [fps, setFps] = useState(0);
  /**
   * 端末へ送る文字。
   * 端末側は IME を通さず入力欄へ直接書き込むので、日本語IMEがローマ字を
   * かな／全角に変換してしまう問題が起きない（adb の input text では起きる）。
   */
  const [textToSend, setTextToSend] = useState('');
  const meta = useRef<{ w: number; h: number }>({ w: 1080, h: 2400 });
  const frameCount = useRef(0);
  const dragStart = useRef<{ x: number; y: number; t: number } | null>(null);

  // 画像描画
  const drawFrame = useCallback((blob: Blob) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const cv = canvasRef.current;
      if (cv) {
        if (cv.width !== img.width || cv.height !== img.height) { cv.width = img.width; cv.height = img.height; }
        cv.getContext('2d')?.drawImage(img, 0, 0);
      }
      URL.revokeObjectURL(url);
      frameCount.current++;
    };
    img.src = url;
  }, []);

  useEffect(() => {
    const token = getToken() ?? '';
    const url = `${WS_BASE}?role=controller&device_id=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'blob';
    wsRef.current = ws;

    ws.onopen = () => setStatus('waiting');
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        try {
          const o = JSON.parse(ev.data);
          if (o.type === 'meta') { meta.current = { w: o.w, h: o.h }; setStatus('streaming'); }
          else if (o.type === 'device_offline') setStatus('offline');
          else if (o.type === 'device_online') setStatus('waiting');
        } catch { /* ignore */ }
      } else {
        setStatus('streaming');
        drawFrame(ev.data as Blob);
      }
    };
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => setStatus('closed');

    const fpsTimer = setInterval(() => { setFps(frameCount.current); frameCount.current = 0; }, 1000);
    return () => { clearInterval(fpsTimer); ws.close(); };
  }, [deviceId, drawFrame]);

  // 座標を 0..1 に正規化（Canvasの実ピクセル基準）
  const norm = (e: React.MouseEvent) => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const send = (obj: object) => { const ws = wsRef.current; if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj)); };

  const onDown = (e: React.MouseEvent) => { const p = norm(e); dragStart.current = { ...p, t: Date.now() }; };
  const onUp = (e: React.MouseEvent) => {
    const s = dragStart.current; dragStart.current = null;
    if (!s) return;
    const p = norm(e);
    const dist = Math.hypot(p.x - s.x, p.y - s.y);
    if (dist < 0.02) send({ type: 'tap', x: p.x, y: p.y });
    else send({ type: 'swipe', x1: s.x, y1: s.y, x2: p.x, y2: p.y, dur: Math.min(600, Date.now() - s.t) });
  };

  const statusText: Record<Status, string> = {
    connecting: '接続中…', waiting: '端末の応答待ち…', streaming: `ライブ ${fps}fps`,
    offline: '端末オフライン', closed: '切断されました',
  };
  const statusColor: Record<Status, string> = {
    connecting: '#d97706', waiting: '#d97706', streaming: '#16a34a', offline: '#dc2626', closed: '#dc2626',
  };

  /** 入力した文字を端末の入力欄へ送る（送信後は欄を空にして続けて打てるようにする） */
  const sendText = () => {
    if (!textToSend) return;
    send({ type: 'text', value: textToSend });
    setTextToSend('');
  };

  const navBtn: React.CSSProperties = { flex: 1, padding: '10px 0', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0' }}>
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #1e293b' }}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>リモート操作</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: statusColor[status] }}>
          ● {statusText[status]}
        </span>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 12 }}>
        <canvas
          ref={canvasRef}
          onMouseDown={onDown}
          onMouseUp={onUp}
          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: 8, border: '1px solid #1e293b', cursor: 'crosshair', background: '#000' }}
        />
      </div>

      {/* 文字入力。端末の入力欄をタップして選んでから送る。
          Wi-Fiのパスワードや社員コードなど、タップだけでは入れられないものに使う。 */}
      <div style={{ padding: '10px 14px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={textToSend}
          onChange={e => setTextToSend(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') sendText(); }}
          placeholder="端末の入力欄をタップしてから、ここに入力して送信"
          style={{ flex: 1, padding: '10px 12px', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, fontSize: 13 }}
        />
        <button style={{ ...navBtn, flex: 'none', padding: '10px 16px' }} onClick={sendText} disabled={!textToSend}>送信</button>
        <button style={{ ...navBtn, flex: 'none', padding: '10px 16px' }} onClick={() => send({ type: 'text', value: '' })} title="入力欄の中身を消す">消去</button>
      </div>

      <div style={{ padding: '10px 14px', display: 'flex', gap: 8, borderTop: '1px solid #1e293b' }}>
        <button style={navBtn} onClick={() => send({ type: 'key', code: 'back' })}>◁ 戻る</button>
        <button style={navBtn} onClick={() => send({ type: 'key', code: 'home' })}>○ ホーム</button>
        <button style={navBtn} onClick={() => send({ type: 'key', code: 'recents' })}>▢ 履歴</button>
      </div>
    </div>
  );
};

export default RemoteControlPage;
